/**
 * 首次登录强制修改密码页面
 */

import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, KeyRound, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { api, getErrorMessage } from '@/services/api';
import { useToast } from '@/components/Toast';
import styles from './ForceChangePassword.module.css';

export default function ForceChangePasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  // 从 location state 获取账号信息
  const account = (location.state as { account?: string })?.account || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // 如果没有账号信息，重定向到登录页
  if (!account) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newPassword.trim()) {
      setError('请输入新密码');
      return;
    }

    if (newPassword.length < 6) {
      setError('新密码长度至少6位');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    setIsSubmitting(true);
    try {
      // 调用修改密码API（首次登录时使用默认密码123456作为旧密码）
      const res = await api.changePassword('123456', newPassword);
      if (res.code === 200) {
        showToast('success', '密码修改成功，正在跳转...');
        // 延迟跳转到首页
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setError(res.message || '密码修改失败');
      }
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* 背景装饰 */}
      <div className={styles.background}>
        <div className={styles.inkWash1} />
        <div className={styles.inkWash2} />
        <div className={styles.inkWash3} />
      </div>

      {/* 修改密码卡片 */}
      <motion.div
        className={styles.card}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* 头部 */}
        <div className={styles.header}>
          <motion.div
            className={styles.logo}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <BookOpen size={32} strokeWidth={1.5} />
          </motion.div>
          <motion.h1
            className={styles.title}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            首次登录
          </motion.h1>
          <motion.p
            className={styles.subtitle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            为了账号安全，请先修改默认密码
          </motion.p>
        </div>

        {/* 提示信息 */}
        <motion.div
          className={styles.hint}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <p>账号：<strong>{account}</strong></p>
          <p>默认密码：<strong>123456</strong></p>
        </motion.div>

        {/* 表单 */}
        <motion.form
          className={styles.form}
          onSubmit={handleSubmit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          {/* 新密码输入 */}
          <div
            className={`${styles.inputGroup} ${focusedField === 'newPassword' ? styles.focused : ''}`}
          >
            <div className={styles.inputIcon}>
              <KeyRound size={18} strokeWidth={1.5} />
            </div>
            <input
              type={showNewPassword ? 'text' : 'password'}
              placeholder="请输入新密码（至少6位）"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onFocus={() => setFocusedField('newPassword')}
              onBlur={() => setFocusedField(null)}
              disabled={isSubmitting}
              autoComplete="new-password"
              autoFocus
            />
            <button
              type="button"
              className={styles.togglePassword}
              onClick={() => setShowNewPassword(!showNewPassword)}
              tabIndex={-1}
              disabled={isSubmitting}
            >
              {showNewPassword ? (
                <EyeOff size={18} strokeWidth={1.5} />
              ) : (
                <Eye size={18} strokeWidth={1.5} />
              )}
            </button>
          </div>

          {/* 确认新密码输入 */}
          <div
            className={`${styles.inputGroup} ${focusedField === 'confirmPassword' ? styles.focused : ''}`}
          >
            <div className={styles.inputIcon}>
              <KeyRound size={18} strokeWidth={1.5} />
            </div>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="请再次输入新密码"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onFocus={() => setFocusedField('confirmPassword')}
              onBlur={() => setFocusedField(null)}
              disabled={isSubmitting}
              autoComplete="new-password"
            />
            <button
              type="button"
              className={styles.togglePassword}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              tabIndex={-1}
              disabled={isSubmitting}
            >
              {showConfirmPassword ? (
                <EyeOff size={18} strokeWidth={1.5} />
              ) : (
                <Eye size={18} strokeWidth={1.5} />
              )}
            </button>
          </div>

          {/* 错误提示 */}
          {error && (
            <motion.div
              className={styles.error}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {error}
            </motion.div>
          )}

          {/* 提交按钮 */}
          <motion.button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            {isSubmitting ? (
              <span className={styles.loading}>修改中...</span>
            ) : (
              <>
                <span>确认修改</span>
                <ArrowRight size={18} strokeWidth={2} />
              </>
            )}
          </motion.button>
        </motion.form>
      </motion.div>

      {/* 底部装饰 */}
      <div className={styles.footer}>
        <div className={styles.seal}>
          <span>墨</span>
        </div>
      </div>
    </div>
  );
}
